import { useState, useCallback, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import Card from "@ui-kit/data-display/Card/Card.web";
import Box from "@ui-kit/layout/Box/Box.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Text from "@ui-kit/typography/Text/Text.web";
import Button from "@ui-kit/form/Button/Button.web";
import Collapsible from "@ui-kit/disclosure/Collapsible/Collapsible.web";
import Icon from "@ui-kit/icons/Icon.web";
import CDNImage from "@shared/components/ui-kit/media/CDNImage/CDNImage.web";
import {
  SectionFormFieldConfig,
  SingleFieldConfig,
  GroupFormFieldConfig,
  RepeaterFormFieldConfig,
  FORM_FIELD_TYPES,
} from "../FormField/types";
import {
  formatPreviewValue,
  getFieldLabel,
  EMPTY_VALUE_PLACEHOLDER,
} from "../utils/preview-formatters";

export interface FormSectionProps {
  /** Section configuration */
  config: SectionFormFieldConfig;
  /** Index of this section (for unique keys) */
  sectionIndex: number;
  /** Whether the form is currently submitting */
  isSubmitting: boolean;
  /** Render function for form fields in edit mode */
  renderFields: (
    fields: SectionFormFieldConfig["fields"],
    sectionIndex: number
  ) => React.ReactNode;
}

/** Field types that can be displayed in preview */
type PreviewableFieldConfig = SingleFieldConfig | RepeaterFormFieldConfig;

/**
 * Extract all previewable field configs from a section's fields, flattening groups
 * Includes both SingleFieldConfig and RepeaterFormFieldConfig
 */
function extractPreviewableFields(
  fields: SectionFormFieldConfig["fields"]
): PreviewableFieldConfig[] {
  return fields.reduce<PreviewableFieldConfig[]>((result, field) => {
    if (field.type === FORM_FIELD_TYPES.GROUP) {
      const groupConfig = field as GroupFormFieldConfig;
      return [...result, ...groupConfig.fields];
    }
    if (field.type === FORM_FIELD_TYPES.CUSTOM) {
      // Skip custom fields in preview
      return result;
    }
    if (field.type === FORM_FIELD_TYPES.REPEATER) {
      return [...result, field as RepeaterFormFieldConfig];
    }
    return [...result, field as SingleFieldConfig];
  }, []);
}

/**
 * Render preview for IMAGE field type - shows actual image thumbnail
 */
function ImagePreview({
  value,
  label,
}: {
  value: unknown;
  label: string;
}): JSX.Element {
  const imageUrl = typeof value === "string" ? value : null;

  return (
    <Box>
      <Text fontSize="xs" color="fg.muted" mb="2xs">
        {label}
      </Text>
      {imageUrl ? (
        <CDNImage
          src={imageUrl}
          alt={label}
          width={100}
          height={48}
          style={{
            objectFit: "contain",
            borderRadius: "4px",
            border: "1px solid var(--chakra-colors-border)",
          }}
        />
      ) : (
        <Text fontSize="sm" color="fg">
          {EMPTY_VALUE_PLACEHOLDER}
        </Text>
      )}
    </Box>
  );
}

/**
 * Render preview for COLOR field type - shows color swatch
 */
function ColorPreview({
  value,
  label,
}: {
  value: unknown;
  label: string;
}): JSX.Element {
  const colorValue = typeof value === "string" && value ? value : null;

  return (
    <Box>
      <Text fontSize="xs" color="fg.muted" mb="2xs">
        {label}
      </Text>
      {colorValue ? (
        <Flex alignItems="center" gap="xs">
          <Box
            width="element.lg"
            height="element.lg"
            borderRadius="sm"
            border="1px solid"
            borderColor="border"
            bg={colorValue}
          />
          <Text fontSize="sm" color="fg">
            {colorValue.toUpperCase()}
          </Text>
        </Flex>
      ) : (
        <Text fontSize="sm" color="fg">
          {EMPTY_VALUE_PLACEHOLDER}
        </Text>
      )}
    </Box>
  );
}

/**
 * Default preview renderer that displays field labels and values in a grid
 */
function DefaultPreviewContent({
  values,
  fieldConfigs,
}: {
  values: Record<string, unknown>;
  fieldConfigs: PreviewableFieldConfig[];
}): JSX.Element {
  // Filter out hidden fields
  const visibleFields = fieldConfigs.filter((f) => !f.hidden);

  return (
    <Box
      display="grid"
      gridTemplateColumns={{ base: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr" }}
      gap="md"
      pt="sm"
    >
      {visibleFields.map((field) => {
        const value = values[field.name];
        const label = getFieldLabel(field);

        // Special rendering for IMAGE fields - show actual image preview
        if (field.type === FORM_FIELD_TYPES.IMAGE) {
          return <ImagePreview key={field.name} value={value} label={label} />;
        }

        // Special rendering for COLOR fields - show color swatch
        if (field.type === FORM_FIELD_TYPES.COLOR) {
          return <ColorPreview key={field.name} value={value} label={label} />;
        }

        // Default text rendering for other field types
        const formattedValue = formatPreviewValue(value, field);

        return (
          <Box key={field.name}>
            <Text fontSize="xs" color="fg.muted" mb="2xs">
              {label}
            </Text>
            <Text fontSize="sm" color="fg">
              {formattedValue}
            </Text>
          </Box>
        );
      })}
    </Box>
  );
}

/**
 * FormSection Component
 *
 * Renders a form section with optional preview/edit toggle functionality.
 * When previewMode is enabled, shows a collapsed preview of values with Edit button.
 * Clicking Edit expands to show full form fields with Save/Cancel buttons.
 */
/**
 * Check if a value is considered "empty" for preview purposes
 */
function isValueEmpty(value: unknown): boolean {
  if (value === undefined || value === null || value === "") return true;
  if (Array.isArray(value) && value.length === 0) return true;
  if (typeof value === "object" && Object.keys(value).length === 0) return true;
  return false;
}

export default function FormSection({
  config,
  sectionIndex,
  isSubmitting,
  renderFields,
}: FormSectionProps): JSX.Element | null {
  const { getValues, trigger, formState } = useFormContext();
  const [isSaving, setIsSaving] = useState(false);

  // Extract previewable field configs (includes SingleFieldConfig and RepeaterFormFieldConfig)
  const previewableFieldConfigs = useMemo(
    () => extractPreviewableFields(config.fields),
    [config.fields]
  );

  // Get field names for this section (for validation)
  const sectionFieldNames = useMemo(
    () => previewableFieldConfigs.map((f) => f.name),
    [previewableFieldConfigs]
  );

  // Check if all section fields are empty (for startInEditModeWhenEmpty)
  const isSectionEmpty = useMemo(() => {
    return sectionFieldNames.every((name) => {
      const value = getValues(name);
      return isValueEmpty(value);
    });
  }, [sectionFieldNames, getValues]);

  // Determine initial editing state:
  // - If previewMode is false, always edit
  // - If startInEditMode is true, always edit (no preview/collapse)
  // - If previewMode is true and startInEditModeWhenEmpty is true (default) and section is empty, edit
  // - Otherwise, show preview
  const startInEditModeWhenEmpty = config.startInEditModeWhenEmpty ?? true;
  const shouldStartInEditMode =
    !config.previewMode ||
    config.startInEditMode === true ||
    (startInEditModeWhenEmpty && isSectionEmpty);

  const [isEditing, setIsEditing] = useState(shouldStartInEditMode);

  // When startInEditMode is true, always show edit form (never preview)
  const effectivelyEditing = config.startInEditMode === true ? true : isEditing;

  // Check if this section has any errors
  const hasErrors = useMemo(() => {
    return sectionFieldNames.some((name) => {
      const error = formState.errors[name];
      return !!error;
    });
  }, [sectionFieldNames, formState.errors]);

  if (config.hidden) return null;

  const { direction = "column", gap = "16px" } = config.layout || {};

  const handleEdit = useCallback(() => {
    setIsEditing(true);
  }, []);

  const handleCancel = useCallback(() => {
    setIsEditing(false);
  }, []);

  const handleSave = useCallback(async () => {
    // Validate only this section's fields
    const isValid = await trigger(sectionFieldNames);

    if (!isValid) {
      return;
    }

    setIsSaving(true);

    try {
      // Call section save callback if provided
      if (config.onSectionSave) {
        const values: Record<string, unknown> = {};
        sectionFieldNames.forEach((name) => {
          values[name] = getValues(name);
        });
        await config.onSectionSave(values);
      }

      // Collapse back to preview (default) unless configured otherwise or startInEditMode
      if (config.collapseOnSave !== false && config.startInEditMode !== true) {
        setIsEditing(false);
      }
    } finally {
      setIsSaving(false);
    }
  }, [config, sectionFieldNames, trigger, getValues]);

  // Get current values for preview
  const currentValues = useMemo(() => {
    const values: Record<string, unknown> = {};
    sectionFieldNames.forEach((name) => {
      values[name] = getValues(name);
    });
    return values;
  }, [sectionFieldNames, getValues]);

  // Render preview content
  const renderPreviewContent = () => {
    if (config.renderPreview) {
      // Pass SingleFieldConfig[] for backward compatibility with custom renderPreview
      const singleFields = previewableFieldConfigs.filter(
        (f): f is SingleFieldConfig => f.type !== FORM_FIELD_TYPES.REPEATER
      );
      return config.renderPreview(currentValues, singleFields);
    }

    return (
      <DefaultPreviewContent
        values={currentValues}
        fieldConfigs={previewableFieldConfigs}
      />
    );
  };

  // Non-preview mode: render standard section
  if (!config.previewMode) {
    return (
      <Card key={`section-${sectionIndex}`}>
        {config.title && (
          <Text fontSize="lg" fontWeight="semibold" mb="2xs">
            {config.title}
          </Text>
        )}
        {config.description && (
          <Text fontSize="sm" color="gray.600" mb="md">
            {config.description}
          </Text>
        )}
        <Flex
          direction={direction}
          gap={gap}
          flexWrap="wrap"
          opacity={isSubmitting ? 0.6 : 1}
          pointerEvents={isSubmitting ? "none" : "auto"}
        >
          {renderFields(config.fields, sectionIndex)}
        </Flex>
      </Card>
    );
  }

  // Preview mode: render with toggle (or expanded only when startInEditMode)
  return (
    <Card key={`section-${sectionIndex}`}>
      {/* Header with title and Edit button (hidden when startInEditMode) */}
      <Flex
        justifyContent="space-between"
        alignItems="flex-start"
        mb={effectivelyEditing ? "md" : "none"}
      >
        <Box>
          {config.title && (
            <Text fontSize="lg" fontWeight="semibold">
              {config.title}
            </Text>
          )}
          {config.description && !effectivelyEditing && (
            <Text fontSize="sm" color="fg.muted" mt="2xs">
              {config.description}
            </Text>
          )}
        </Box>

        {!effectivelyEditing && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleEdit}
            leftIcon={<Icon name="Edit" boxSize="element.md" />}
            data-testid={`section-${sectionIndex}-edit-button`}
          >
            {config.editButtonText || "Edit"}
          </Button>
        )}
      </Flex>

      {/* Preview content (visible when not editing) */}
      <Collapsible open={!effectivelyEditing} animateOpacity>
        {renderPreviewContent()}
      </Collapsible>

      {/* Edit content (visible when editing) */}
      <Collapsible open={effectivelyEditing} animateOpacity>
        {config.description && (
          <Text fontSize="sm" color="fg.muted" mb="md">
            {config.description}
          </Text>
        )}

        <Flex
          direction={direction}
          gap={gap}
          flexWrap="wrap"
          opacity={isSubmitting || isSaving ? 0.6 : 1}
          pointerEvents={isSubmitting || isSaving ? "none" : "auto"}
        >
          {renderFields(config.fields, sectionIndex)}
        </Flex>

        {/* Section footer with Save/Cancel buttons */}
        <Flex
          justifyContent="flex-end"
          gap="sm"
          mt="lg"
          pt="md"
          borderTop="1px solid"
          borderTopColor="border"
        >
          {!config.hideCancelButton && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleCancel}
              isDisabled={isSaving}
              data-testid={`section-${sectionIndex}-cancel-button`}
            >
              {config.cancelButtonText || "Cancel"}
            </Button>
          )}
          <Button
            variant="solid"
            colorPalette="primary"
            size="sm"
            onClick={handleSave}
            isLoading={isSaving}
            loadingText="Saving..."
            isDisabled={hasErrors}
            data-testid={`section-${sectionIndex}-save-button`}
          >
            {config.saveButtonText || "Save"}
          </Button>
        </Flex>
      </Collapsible>
    </Card>
  );
}

/**
 * PreviewField component for use in custom renderPreview functions
 * Provides consistent styling for label/value pairs
 */
FormSection.PreviewField = function PreviewField({
  label,
  value,
  emptyText = EMPTY_VALUE_PLACEHOLDER,
}: {
  label: string;
  value: unknown;
  emptyText?: string;
}): JSX.Element {
  const displayValue =
    value === undefined || value === null || value === ""
      ? emptyText
      : String(value);

  return (
    <Box>
      <Text fontSize="xs" color="fg.muted" mb="2xs">
        {label}
      </Text>
      <Text fontSize="sm" color="fg">
        {displayValue}
      </Text>
    </Box>
  );
};
